import React, { useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';
import { useBeta } from '../hooks/useBeta';
import { Sprite } from './sprite';
import { useAppName } from '../hooks/useAppName';
import { useAuth } from '../hooks/useAuth';
import useAlertStore from '../store/alert';
import { useAuthStore } from '../store/auth-store';
import { Role } from '../services/auth-service/types';

interface NavLinkProps extends LinkProps {
  icon: string;
}

const NavLink: React.FC<NavLinkProps> = ({
  icon,
  children,
  to,
  ...props
}: NavLinkProps) => {
  const location = useLocation();

  const current = useMemo(() => location.pathname === to, [location, to]);

  return (
    <Link to={to} {...props}>
      <div
        className={`nav-link flex flex-row px-3 py-2.5 rounded-lg ${
          current && 'bg-blue-light'
        } text-blue fill-blue hover:bg-blue-light font-medium transition duration-150`}>
        <div className="pr-3">
          <Sprite
            name={icon}
            className="w-[24px] h-[24px] fill-blue text-blue"
          />
        </div>
        <div className="flex-1">{children}</div>
      </div>
    </Link>
  );
};

export const Menu: React.FC = () => {
  const isBeta = useBeta();
  const appName = useAppName();
  const { idToken } = useAuthStore();
  const cUser = useMemo(() => idToken?.payload, [idToken]);
  const { signOut, isAuthenticated } = useAuth();
  const { alert } = useAlertStore();
  const navigate = useNavigate();

  const onSignOut = useCallback(async () => {
    alert('Confirmation', `Are you sure you want to log out?`, [
      { text: 'No', style: { color: '#003E74', backgroundColor: '#ccc' } },
      {
        text: 'Yes',
        onClick: async () => {
          signOut();
          navigate('/auth/login');
        },
      },
    ]);
  }, [alert, navigate, signOut]);

  const isPhysician = useMemo(() => {
    if (!cUser?.['custom:roles']) return false;
    const roles = cUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.Physician];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [cUser]);

  const isSuperUser = useMemo(() => {
    if (!cUser?.['custom:roles']) return false;
    const roles = cUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.SuperUser];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [cUser]);

  const isEventManager = useMemo(() => {
    if (!cUser?.['custom:roles']) return false;
    const roles = cUser['custom:roles'].split(',') as Role[];
    const rolesAccess = [Role.EventManager];
    let canAccess = false;
    roles.forEach(role => {
      if (rolesAccess.includes(role)) {
        canAccess = true;
      }
    });
    return canAccess;
  }, [cUser]);

  return (
    <nav
      className="sticky top-0 pt-6 flex flex-col min-h-screen"
      style={{ zIndex: 2 }}>
      <div className="flex flex-row px-6">
        <div>
          <Sprite
            name="bhd-logo"
            className="md:block hidden h-[48px] w-[48px]"
          />
        </div>
        <div className="relative h-full pl-2">
          <div className="flex flex-col justify-center py-[6px]">
            <div className="flex flex-row">
              <h3 className="font-bold text-xl leading-7 text-black">
                {appName}
              </h3>
              <div className="pl-1 flex flex-col justify-start">
                {isBeta && (
                  <div className="rounded-full leading-4 bg-white-dirty-light font-semibold text-[10px] px-1 border border-gray-light">
                    BETA
                  </div>
                )}
              </div>
            </div>
            <div>
              <h3 className="text-xs leading-[0.9rem] font-medium text-gray-dark tracking-[0.08em] mt-[2px]">
                ADMIN
              </h3>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 pt-11 px-6">
        <NavLink icon="icon-cart" to="/orders">
          Orders
        </NavLink>
        {!isEventManager && (
          <NavLink icon="icon-cart" to="/gifts">
            Gifts
          </NavLink>
        )}
        {!isPhysician ? (
          <>
            <NavLink icon="icon-drawer" to="/events">
              Events
            </NavLink>
            {!isEventManager ? (
              <NavLink icon="icon-customers" to="/customers">
                Users
              </NavLink>
            ) : null}
          </>
        ) : null}
        <NavLink icon="icon-cart" to="/kits">
          Kits
        </NavLink>
        {isSuperUser ? (
          <NavLink icon="icon-list" to="/lab-result-verbiages">
            Verbiages CRUD
          </NavLink>
        ) : null}
      </div>
      <div className="border-t border-gray-medium min-h-[64px] px-6 py-2.5">
        {isAuthenticated && (
          <button type="button" onClick={onSignOut} className="w-full">
            <div className="flex flex-row px-3 py-2.5 text-gray-dark rounded-lg hover:bg-blue-light hover:text-black font-medium transition duration-150">
              <div className="pr-3">
                <Sprite name="icon-shutdown" className="w-[24px] h-[24px]" />
              </div>
              <div className="flex-1 text-left">Logout</div>
            </div>
          </button>
        )}
      </div>
    </nav>
  );
};
